import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { ChoosePurchaseModelDataTypes } from "../../../../Schema/choosePurchaseModel.types";
import { queryClient } from "../../../store";
import { closeModal } from "../../modal/modal";
import Cookies from "js-cookie";

export type BuySubscriptionPlanLoadAction = {
  type: string;
  payload: {
    plan_id: number;
    discount_code?: string;
    use_wallet?: boolean;
    token_id: string | number;
  };
};

export type BuySubscriptionPlanData = {
  data: ChoosePurchaseModelDataTypes;
};

const BuySubscriptionPlan = new ReduxFetchState<
  BuySubscriptionPlanData,
  { plan_id: number; discount_code?: string; use_wallet: boolean; token_id: string | number },
  any
>("buySubscriptionPlan");

export function* watchBuySubscriptionPlan(action: BuySubscriptionPlanLoadAction) {
  try {
    const { discount_code, token_id, use_wallet } = action.payload;

    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: BuySubscriptionPlanData = yield axios({
      headers: {
        lang,
      },
      url: `/api/v1/ai/subscription/buy-plan${discount_code ? "?discount_code=" + discount_code : ""}`,
      method: "POST",
      data: {
        plan_id: token_id,
        use_wallet,
        callback_url: `${window.location.origin}/${lang}/profile`,
      },
    });

    queryClient.removeQueries({ queryKey: ["checkLogin", lang] });
    Cookies.remove("checkLogin", { domain: ".lamtakam.com" });
    queryClient.removeQueries({ queryKey: ["getSubscriptionPlans", lang] });
    queryClient.removeQueries({ queryKey: ["getModelList", lang] });

    if (data.data.redirect_link) {
      window.open(data.data.redirect_link, "_self");
    } else {
      yield put(closeModal());
      alert({ message: data?.data?.message, type: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    yield put(BuySubscriptionPlan.actions.loadSuccess({ data: data.data }));
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(BuySubscriptionPlan.actions.loadFailure(e.message));
  }
}

export function* buySubscriptionPlanSagas() {
  yield takeEvery(BuySubscriptionPlan.actionTypes.load, watchBuySubscriptionPlan);
}

export function useBuySubscriptionPlan() {
  const { loading, data, ...buySubscriptionPlanState } = useAppSelector(
    (state) => state.buySubscriptionPlan,
  );
  const dispatch = useAppDispatch();

  const dispatchBuySubscriptionPlan = useCallback(
    (data: {
      plan_id: number;
      discount_code?: string;
      token_id: string | number;
      use_wallet: boolean;
    }) => {
      dispatch(BuySubscriptionPlan.actions.load(data));
    },
    [dispatch],
  );

  return {
    ...buySubscriptionPlanState,
    dispatchBuySubscriptionPlan,
    buySubscriptionPlanLoading: !!(loading || data?.data?.redirect_link),
    buySubscriptionPlanData: data,
  };
}

export const {
  actionTypes: buySubscriptionPlanActionsTypes,
  actions: buySubscriptionPlanActions,
  reducer: buySubscriptionPlanReducer,
} = BuySubscriptionPlan;
